import React from "react";
import { Link } from "react-router-dom";
import {
  FaFacebookF,
  FaInstagram,
  FaLinkedinIn,
} from "react-icons/fa";
import { FaXTwitter } from "react-icons/fa6";
import { Lock, Mail, Phone, Send, MapPin, ChevronRight } from "lucide-react";
import logoImg from "./assets/devdeepam_logo1.png";

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const socialIcons = [
    { Icon: FaFacebookF, label: "Facebook", href: "#", hoverColor: "hover:bg-[#1877F2] hover:border-[#1877F2]" },
    { Icon: FaXTwitter, label: "X (Twitter)", href: "#", hoverColor: "hover:bg-white hover:text-slate-900 hover:border-white" },
    { Icon: FaInstagram, label: "Instagram", href: "#", hoverColor: "hover:bg-[#E1306C] hover:border-[#E1306C]" },
    { Icon: FaLinkedinIn, label: "LinkedIn", href: "#", hoverColor: "hover:bg-[#0077B5] hover:border-[#0077B5]" },
  ];

  const quickLinks = [
    { label: "About Company", path: "/about" },
    { label: "Contact Us", path: "/contact" },
    { label: "Solar Calculator", path: "/calculator" },
    { label: "Privacy Policy", path: "#privacy" },
  ];

  const solutions = [
    { label: "Residential Solar", path: "/servicespage" },
    { label: "Commercial Setup", path: "/servicespage" },
    { label: "Industrial Projects", path: "/servicespage" },
    { label: "Maintenance Services", path: "/servicespage" },
  ];

  return (
    <footer className="bg-[#050B14] text-slate-300 relative border-t-[3px] border-[#F7941D] font-sans">
      
      {/* 🌟 Top Subtle Gradient Line */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#F7941D] via-[#FFC107] to-[#4CAF3E] opacity-80" />

      {/* ════════ MAIN CONTENT ════════ */}
      <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 pt-12 pb-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-x-8 gap-y-10">
          
          {/* ═══ LEFT: BRAND + DESCRIPTION (Col-Span-4) ═══ */}
          <div className="lg:col-span-4 flex flex-col space-y-5">
            {/* Logo */}
            <div className="bg-white/5 inline-block p-2.5 rounded-xl border border-white/10 w-fit">
              <img
                src={logoImg}
                alt="DevDeepam Solar"
                className="h-10 sm:h-12 w-auto object-contain brightness-110"
              />
            </div>
            <p className="text-[13px] text-slate-400 leading-relaxed max-w-sm">
              Empowering homes and businesses with top-tier solar solutions. Clean energy, zero emissions, and massive savings for a sustainable future.
            </p>
            <p className="text-[13px] text-slate-400 leading-relaxed max-w-sm">
              At DevDeepam Solar, we believe in creating a greener tomorrow by harnessing the power of the sun today. Join us in the journey toward a cleaner, brighter, and more sustainable planet.
            </p>
          </div>

          {/* ═══ CENTER: QUICK LINKS + SOLUTIONS + ADMIN LOGIN (Col-Span-5) ═══ */}
          <div className="lg:col-span-5 flex flex-col">
            <div className="grid grid-cols-2 gap-x-8 gap-y-6">
              {/* Quick Links */}
              <div>
                <h4 className="text-white text-[15px] font-bold mb-5 flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-[#F7941D]" /> Quick Links
                </h4>
                <ul className="space-y-3.5">
                  {quickLinks.map((link, idx) => (
                    <li key={idx}>
                      <Link to={link.path} className="text-[13px] text-slate-400 hover:text-[#F7941D] flex items-center gap-2 transition-all group">
                        <ChevronRight className="w-3.5 h-3.5 text-[#1E5CB3] group-hover:text-[#F7941D] transition-colors" />
                        <span>{link.label}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Solutions */}
              <div>
                <h4 className="text-white text-[15px] font-bold mb-5 flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-[#F7941D]" /> Our Solutions
                </h4>
                <ul className="space-y-3.5">
                  {solutions.map((link, idx) => (
                    <li key={idx}>
                      <Link to={link.path} className="text-[13px] text-slate-400 hover:text-[#F7941D] flex items-center gap-2 transition-all group">
                        <ChevronRight className="w-3.5 h-3.5 text-[#1E5CB3] group-hover:text-[#F7941D] transition-colors" />
                        <span>{link.label}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Admin Login - Centered Below */}
            <div className="mt-5 flex justify-left">
              <Link 
                to="/login" 
                className="inline-flex items-center gap-2 px-5 py-2 rounded-lg bg-[#0B1526] border border-slate-700/50 text-[13px] text-slate-400 hover:text-[#F7941D] hover:border-[#F7941D] transition-all"
              >
                <Lock className="w-3.5 h-3.5" />
                <span>Admin Login</span>
              </Link>
            </div>
          </div>

          {/* ═══ RIGHT: CONTACT + NEWSLETTER + SOCIAL (Col-Span-3) ═══ */}
          <div className="lg:col-span-3 flex flex-col space-y-5">
            <h4 className="text-white text-[15px] font-bold flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-[#F7941D]" /> Get In Touch
            </h4>

            {/* Contact Info */}
            <div className="space-y-3">
              <a href="mailto:info@devdeepamsolar.com" className="flex items-start gap-3 text-[13px] hover:text-[#F7941D] transition-colors group">
                <div className="w-8 h-8 flex-shrink-0 rounded-full bg-[#1E5CB3]/10 flex items-center justify-center text-[#F7941D] group-hover:bg-[#F7941D] group-hover:text-white transition-colors">
                  <Mail className="w-4 h-4" />
                </div>
                <span className="pt-1.5">info@devdeepamsolar.com</span>
              </a>
              <a href="tel:+91 97557 94514" className="flex items-start gap-3 text-[13px] hover:text-[#F7941D] transition-colors group">
                <div className="w-8 h-8 flex-shrink-0 rounded-full bg-[#1E5CB3]/10 flex items-center justify-center text-[#F7941D] group-hover:bg-[#F7941D] group-hover:text-white transition-colors">
                  <Phone className="w-4 h-4" />
                </div>
                <span className="pt-1.5">+91  97557 94514</span>
              </a>
              <div className="flex items-start gap-3 text-[13px] text-slate-400 group">
                <div className="w-8 h-8 flex-shrink-0 rounded-full bg-[#1E5CB3]/10 flex items-center justify-center text-[#F7941D]">
                  <MapPin className="w-4 h-4" />
                </div>
                <span className="pt-1 leading-relaxed">
                Sector 8, behind central avenue, Kamal Vihar, Raipur, Chhattisgarh 492015
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ════════ FOOTER BOTTOM ════════ */}
      <div className="border-t border-slate-800 bg-[#030811]">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 py-5 flex flex-col sm:flex-row justify-between items-center gap-3">
          <p className="text-[12px] text-slate-500 text-center sm:text-left">
            &copy; {currentYear} Devdeepam Renewable Systems Pvt. Ltd. All rights reserved.
          </p>
          <div className="flex items-center gap-1.5 text-[12px] text-slate-500">
            <span>Powered by</span>
            <a 
              href="https://yashvitech.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-slate-400 hover:text-[#F7941D] font-semibold transition-colors"
            >
              Yashvitech IT Solution
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;